import { useState } from "react";
import { Search, X, Calendar, Tag, CloudRain, Smile, SlidersHorizontal, FolderOpen, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import MultiTagSelector from "@/components/multi-tag-selector";
import { format } from "date-fns";

export interface JournalFilters {
  search: string;
  tags: string[];
  mood: string;
  weather: string;
  category: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  pinnedOnly: boolean;
}

interface JournalSearchFilterProps {
  filters: JournalFilters;
  onFiltersChange: (filters: JournalFilters) => void;
  availableTags: string[];
  categories: { id: string; name: string; color: string }[];
}

const MOOD_OPTIONS = [
  { value: "all", label: "All Moods" },
  { value: "happy", label: "😊 Happy" },
  { value: "sad", label: "😢 Sad" },
  { value: "excited", label: "🤩 Excited" },
  { value: "anxious", label: "😰 Anxious" },
  { value: "calm", label: "😌 Calm" },
  { value: "angry", label: "😠 Angry" },
  { value: "grateful", label: "🙏 Grateful" },
  { value: "tired", label: "😴 Tired" },
];

const WEATHER_OPTIONS = [
  { value: "all", label: "All Weather" },
  { value: "sunny", label: "☀️ Sunny" },
  { value: "cloudy", label: "☁️ Cloudy" },
  { value: "rainy", label: "🌧️ Rainy" },
  { value: "snowy", label: "❄️ Snowy" },
  { value: "stormy", label: "⛈️ Stormy" },
  { value: "windy", label: "💨 Windy" },
];

export default function JournalSearchFilter({
  filters,
  onFiltersChange,
  availableTags,
  categories,
}: JournalSearchFilterProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleTagsChange = (tags: string[]) => {
    onFiltersChange({ ...filters, tags });
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = filters.tags.filter(t => t !== tag);
    onFiltersChange({ ...filters, tags: newTags });
  };

  const handleMoodChange = (value: string) => {
    onFiltersChange({ ...filters, mood: value === "all" ? "" : value });
  };

  const handleWeatherChange = (value: string) => {
    onFiltersChange({ ...filters, weather: value === "all" ? "" : value });
  };

  const handleCategoryChange = (value: string) => {
    onFiltersChange({ ...filters, category: value === "all" ? "" : value });
  };

  const handleStartDateChange = (date: Date | undefined) => {
    onFiltersChange({ ...filters, startDate: date });
  };

  const handleEndDateChange = (date: Date | undefined) => {
    onFiltersChange({ ...filters, endDate: date });
  };

  const handlePinnedToggle = () => {
    onFiltersChange({ ...filters, pinnedOnly: !filters.pinnedOnly });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      search: "",
      tags: [],
      mood: "",
      weather: "",
      category: "",
      startDate: undefined,
      endDate: undefined,
      pinnedOnly: false,
    });
  };

  const hasActiveFilters = 
    filters.search || 
    filters.tags.length > 0 || 
    filters.mood || 
    filters.weather || 
    filters.category ||
    filters.startDate || 
    filters.endDate ||
    filters.pinnedOnly;

  return (
    <div className="space-y-4">
      {/* Main Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            type="text"
            placeholder="Search journal entries..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
            data-testid="input-journal-search"
          />
        </div>
        <Button
          variant={filters.pinnedOnly ? "default" : "outline"}
          size="icon"
          onClick={handlePinnedToggle}
          title={filters.pinnedOnly ? "Show all entries" : "Show only starred entries"}
          data-testid="button-toggle-starred-filter"
        >
          <Star className={`w-4 h-4 ${filters.pinnedOnly ? 'fill-current' : ''}`} />
        </Button>
        <Button
          variant={showAdvanced ? "default" : "outline"}
          size="icon"
          onClick={() => setShowAdvanced(!showAdvanced)}
          data-testid="button-toggle-advanced-filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
        </Button>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearAllFilters}
            title="Clear all filters"
            data-testid="button-clear-filters"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="bg-muted/30 p-4 rounded-lg space-y-4 border border-border">
          {/* Mood Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Smile className="w-4 h-4" />
              Mood
            </label>
            <Select value={filters.mood || "all"} onValueChange={handleMoodChange}>
              <SelectTrigger data-testid="select-mood-filter">
                <SelectValue placeholder="All Moods" />
              </SelectTrigger>
              <SelectContent>
                {MOOD_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Weather Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <CloudRain className="w-4 h-4" />
              Weather
            </label>
            <Select value={filters.weather || "all"} onValueChange={handleWeatherChange}>
              <SelectTrigger data-testid="select-weather-filter">
                <SelectValue placeholder="All Weather" />
              </SelectTrigger>
              <SelectContent>
                {WEATHER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              Category
            </label>
            <Select value={filters.category || "all"} onValueChange={handleCategoryChange}>
              <SelectTrigger data-testid="select-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                {categories.filter(c => c.name).map((category) => (
                  <SelectItem key={category.id} value={category.name!}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Start Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="button-start-date-filter"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {filters.startDate ? format(filters.startDate, "PP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.startDate}
                  onSelect={handleStartDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              End Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="button-end-date-filter"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {filters.endDate ? format(filters.endDate, "PP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={filters.endDate}
                  onSelect={handleEndDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </label>
              <MultiTagSelector
                availableTags={availableTags}
                selectedTags={filters.tags}
                onTagsChange={handleTagsChange}
                triggerButton={
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    data-testid="button-select-tags"
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    Select Tags
                    {filters.tags.length > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {filters.tags.length}
                      </Badge>
                    )}
                  </Button>
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {filters.pinnedOnly && (
            <Badge variant="secondary" className="gap-1">
              ⭐ Starred only
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={handlePinnedToggle}
              />
            </Badge>
          )}
          {filters.search && (
            <Badge variant="secondary" className="gap-1">
              Search: {filters.search}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleSearchChange("")}
              />
            </Badge>
          )}
          {filters.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1">
              {tag}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleRemoveTag(tag)}
              />
            </Badge>
          ))}
          {filters.mood && (
            <Badge variant="secondary" className="gap-1">
              Mood: {MOOD_OPTIONS.find(m => m.value === filters.mood)?.label}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleMoodChange("")}
              />
            </Badge>
          )}
          {filters.weather && (
            <Badge variant="secondary" className="gap-1">
              Weather: {WEATHER_OPTIONS.find(w => w.value === filters.weather)?.label}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleWeatherChange("")}
              />
            </Badge>
          )}
          {filters.category && (
            <Badge variant="secondary" className="gap-1">
              Category: {filters.category}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleCategoryChange("all")}
              />
            </Badge>
          )}
          {filters.startDate && (
            <Badge variant="secondary" className="gap-1">
              From: {format(filters.startDate, "PP")}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleStartDateChange(undefined)}
              />
            </Badge>
          )}
          {filters.endDate && (
            <Badge variant="secondary" className="gap-1">
              To: {format(filters.endDate, "PP")}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => handleEndDateChange(undefined)}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
